import React, { useEffect, useState } from 'react';
import ModalWrapper from '../../components/elements/ModalWrapper';
import { Checkbox, Divider, Grid, Text, Textarea } from '@mantine/core';
import { showNotification } from '../../utilities/mantineNotification';
import {
  getDataInterestUser,
  getMasterInterest,
  postInterest
} from '../../services/navbar';
import { useMutation, useQuery } from '@tanstack/react-query';
import useLocalStorage from '../../hooks/useUserData';
import Button from '../../components/elements/Button';
import * as Unicons from '@iconscout/react-unicons';

interface ModalCHangeInterestProps {
  opened: boolean;
  isSurvey?: boolean;
  onClose: () => void;
}

const ModalChangeInterest: React.FC<ModalCHangeInterestProps> = ({
  opened,
  onClose
}) => {
  const [userData, setUserData] = useLocalStorage('persist:root', null);
  const profileUser = JSON.parse(userData?.auth);
  const [isOther, setIsOther] = useState(false);
  const profile = profileUser?.userData;
  const [checkedInterests, setCheckedInterests] = useState({});
  const [otherInterest, setOtherInterest] = useState('');

  const fetchedMasterInterest = useQuery({
    queryKey: [`masterInterest`],
    queryFn: () => getMasterInterest(),
    enabled: true
  });

  const fetchedDataInterestUser = useQuery({
    queryKey: [`interestUser`],
    queryFn: () => getDataInterestUser(profile?.email),
    enabled: !!profile?.email
  });

  const saveInterest = useMutation({
    mutationFn: postInterest,
    onSuccess: (response) => {
      if (
        response?.data?.resultCode === 200 &&
        response?.data?.status !== '0'
      ) {
        showNotification({
          type: 'success',
          title: 'Yeay! we`re saved your interest',
          message: 'We`ll give you best recommendation for you'
        });
        onClose();
        fetchedDataInterestUser.refetch();
      } else {
        showNotification({
          type: 'error',
          title: 'Oh no! something wrong when saved your interest',
          message: 'Failed when save interest. Please try again'
        });
      }
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const currentUserInterest =
    fetchedDataInterestUser?.data?.data?.data?.currentInterest;
  const masterInterest = fetchedMasterInterest?.data?.data?.data;
  const latestPeriodID =
    fetchedDataInterestUser?.data?.data?.data?.latestPeriodID;

  const [otherInterestList, setOtherInterestList] = useState([]);

  useEffect(() => {
    // Initialize checked state based on currentUserInterest
    if (currentUserInterest?.length > 0) {
      // filter currentUserInterest that has isStandard = "1"
      const standardInterest = currentUserInterest.filter(
        (item) => item.isstandard === '1'
      );
      const checkedInterestsMap = {};
      standardInterest.forEach((item) => {
        checkedInterestsMap[item.interestID] = true;
      });
      setCheckedInterests(checkedInterestsMap);

      // filter currentUserInterest that has isStandard = "0"
      const otherInterestLt = currentUserInterest
        .filter((item) => item.isstandard === '0')
        .map((item) => ({ ...item, isChecked: true }));

      // Now each object in otherInterestLt has an isChecked property set to true

      setOtherInterestList(otherInterestLt);
    }
  }, [currentUserInterest]);

  const handleSubmit = () => {
    if (
      Object.values(checkedInterests).filter(Boolean).length +
        otherInterestList.filter((item) => item.isChecked).length >
      3
    ) {
      showNotification({
        type: 'error',
        title: 'Oh no! something wrong when saved your interest',
        message: 'Please select a maximum of 3 topics of interest'
      });
      return;
    }

    const selectedInterests = masterInterest.filter(
      (item) => checkedInterests[item.id]
    );

    const _toStringData = JSON.stringify(selectedInterests);
    const _normalizeData = _toStringData.replace(/\\/g, '');

    const parsedData = JSON.parse(_normalizeData);
    const normalizedData = parsedData.map((item) => ({ id: item.id }));

    const _toStringNormalizeData = JSON.stringify(normalizedData);
    const _passData = _toStringNormalizeData.replace(/\\/g, '');

    // filter otherInterestList that has isChecked = true
    const otherInterestLt = otherInterestList
      .filter((item) => item.isChecked)
      .map((item) => item.interestName);
    const otherInterestJoined = otherInterestLt.join(', ');

    const payload = {
      email: profile?.email,
      dataJson: _passData,
      targetInterestPeriodID: latestPeriodID,
      otherInterests: otherInterestJoined
    };

    saveInterest.mutate(payload);
  };

  const disabledChecked = () => {
    return (
      Object.values(checkedInterests).filter(Boolean).length +
        otherInterestList.filter((item) => item.isChecked).length >=
      3
    );
  };

  const disabled = () => {
    return Object.values(checkedInterests).filter(Boolean).length < 1;
  };

  const handleCheckboxChange = (interestID) => {
    if (
      Object.values(checkedInterests).filter(Boolean).length +
        otherInterestList.filter((item) => item.isChecked).length <
        3 ||
      checkedInterests[interestID]
    ) {
      setCheckedInterests((prevState) => ({
        ...prevState,
        [interestID]: !prevState[interestID]
      }));
    }
  };

  const handleOtherCheckboxChange = (interestID) => {
    const newOtherInterestList = otherInterestList.map((item) => {
      if (item.interestID === interestID) {
        return { ...item, isChecked: !item.isChecked };
      }
      return item;
    });

    setOtherInterestList(newOtherInterestList);
  };

  return (
    <ModalWrapper
      title="Change interest"
      isVisible={opened}
      onClose={onClose}
      size={'xl'}
    >
      <Text className="text-md-bold">
        What type of topics you like to heard?
      </Text>
      <Text className="text-xs-medium text-grey-80">
        Please select a maximum of 3 topics of interest that are appropriate for
        you{' '}
        <b
          style={{
            color:
              Object.values(checkedInterests).filter(Boolean).length +
                otherInterestList.filter((item) => item.isChecked).length ===
              3
                ? 'green'
                : 'red'
          }}
        >
          (
          {Object.values(checkedInterests).filter(Boolean).length +
            otherInterestList.filter((item) => item.isChecked).length}{' '}
          of 3 selected)
        </b>
      </Text>

      <Grid gutter={8} my={24}>
        {masterInterest?.length > 0 &&
          masterInterest.map((item, index) => {
            return (
              <Grid.Col key={index} span={{ base: 12, md: 4 }}>
                <Checkbox
                  mt={10}
                  checked={checkedInterests[item.id] || false}
                  label={item.name}
                  disabled={disabledChecked() && !checkedInterests[item.id]}
                  onChange={() => handleCheckboxChange(item.id)}
                />
              </Grid.Col>
            );
          })}
      </Grid>
      {otherInterestList?.length > 0 && (
        <>
          <Divider my={'md'} />
          <Text fw={700}>Another Interest</Text>
          <Text className="text-xs-medium text-grey-80">
            (THIS IS OPTIONAL) Please write down your interest here if there is
            none of your interest in the list above, and use comma (,) to
            separate each interest
          </Text>
          <Grid gutter={8} my={24}>
            {otherInterestList?.length > 0 &&
              otherInterestList.map((item, index) => {
                return (
                  <Grid.Col key={index} span={{ base: 12, md: 4 }}>
                    <Checkbox
                      mt={10}
                      checked={item.isChecked}
                      label={item.interestName}
                      onChange={() =>
                        handleOtherCheckboxChange(item.interestID)
                      }
                    />
                  </Grid.Col>
                );
              })}
          </Grid>
        </>
      )}

      <Button
        onClick={handleSubmit}
        loading={saveInterest.isLoading}
        disabled={disabled()}
        leftSection={<Unicons.UilSave />}
      >
        Submit
      </Button>
    </ModalWrapper>
  );
};

export default ModalChangeInterest;
